import React, { useEffect, useState, useRef, } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import api from '../../../shared/api/axios';
import SidebarVisitor from '../../sidebars/SidebarVisitor';
import { useLikes, likeKeyFromRow } from '../../../shared/contexts/LikesContext';
import { useTranslation } from 'react-i18next';
import VisitorPostingsPresentation from '../MyProfile/VisitorPostingsPresentation';
import Hls from "hls.js";
import Modal from "../../settings/Modal";

type MediaEntry = {
  kind: 'image' | 'video';
  url?: string;
  isHls?: boolean;
  videoId?: string;
  hlsUrl?: string | null;
  dashUrl?: string | null;
  posterUrl?: string | null;
  musicId?: string | null;
  musicTitle?: string | null;
  musicArtist?: string | null;
};


export type Post ={
  _id: string;
  likeKey: string;
  title?: string;
  titleColor?: string;
  media: MediaEntry[];
  createdAt: string;
  location?: {
    city?: string;
    country?: string;
  };
  type: 'media';
  likes: number;
  likedByUser?: boolean;
}

// Api base URL normalization
const API_BASE = process.env.REACT_APP_API_BASE_URL as string;
const API_ORIGIN = API_BASE.replace(/\/api\/?$/, "");

const IMAGE_CDN = process.env.REACT_APP_PUBLIC_IMAGE_BASE_URL as string;
const VIDEO_CDN = process.env.REACT_APP_PUBLIC_VIDEO_BASE_URL as string;

if (!IMAGE_CDN) {
  throw new Error("REACT_APP_PUBLIC_IMAGE_BASE_URL is not defined");
}
if (!VIDEO_CDN) {
  throw new Error("REACT_APP_PUBLIC_VIDEO_BASE_URL is not defined");
}

const toImageUrl = (p?: string): string => {
  if (!p) return "";
  if (/^(https?:|data:)/i.test(p)) return p;
  return `${IMAGE_CDN.replace(/\/$/, "")}/${p.replace(/^\/+/, "")}`;
};

const resolveVideoUrl = (m: MediaEntry): string => {
  if (m.hlsUrl) return m.hlsUrl;
  if (m.url && /^https?:\/\//i.test(m.url)) return m.url;
  if (m.videoId) return `${API_ORIGIN}/hls/playlist/${m.videoId}`;
  return '';
};

const hlsUrl = (videoId: string) =>
  `${API_ORIGIN}/hls/playlist/${videoId}`;

export const HLSPlayer = ({
  src,
  poster,
  muted,
  active,
  style = {},
  onOrientation,
  isLandscape
}: {
  src: string;
  poster?: string;
  muted: boolean;
  active: boolean;
  style?: React.CSSProperties;
  onOrientation?: (isLandscape: boolean) => void;
  isLandscape?: boolean | null;
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);

  const [showControls, setShowControls] = useState(false);
  const [progress, setProgress] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isPlaying, setIsPlaying] = useState(false);
const [isMuted, setIsMuted] = useState(muted);
const [userPaused, setUserPaused] = useState(false);

  const emitOrientation = (v: HTMLVideoElement) => {
    if (!v.videoWidth || !v.videoHeight) return;
    onOrientation?.(v.videoWidth > v.videoHeight);
  };

  //Play/pause
  const PlayIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="#bcb4b4"
  >
    <path d="M7 5v14l11-7z" />
  </svg>
);

const PauseIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="#bcb4b4"
  >
    <rect x="7.2" y="5" width="3.6" height="14" rx="1.2" />
    <rect x="13.2" y="5" width="3.6" height="14" rx="1.2" />
  </svg>
);

  //Loudspeakers
  const SpeakerOnIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#bbb"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
    <path d="M15 9a4 4 0 0 1 0 6" />
    <path d="M18 7a7 7 0 0 1 0 10" />
  </svg>
);


const SpeakerOffIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#bbb"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
    <line x1="23" y1="9" x2="17" y2="15" />
    <line x1="17" y1="9" x2="23" y2="15" />
  </svg>
);

  /* ---------------- HLS attach ---------------- */
useEffect(() => {
  const video = videoRef.current;
  if (!video || !src) return;

  let hls: Hls | null = null;

  if (video.canPlayType("application/vnd.apple.mpegurl")) {
    video.src = src;
    video.load();
  } else if (Hls.isSupported()) {
    hls = new Hls({ startLevel: 0, enableWorker: true });
    hls.loadSource(src);
    hls.attachMedia(video);
  }

  return () => {
    try {
      if (hls) hls.destroy();
      video.pause();
      video.removeAttribute("src");
      video.load();
    } catch {}
  };
}, [src]);

  /* ---------------- autoplay logic ---------------- */
 useEffect(() => {
  const v = videoRef.current;
  if (!v) return;

  if (!active) {
    v.pause();
    return;
  }

  if (!userPaused) {
    v.play().catch(() => {});
  }
}, [active, userPaused]);

  /* ---------------- progress tracking ---------------- */
  const onTimeUpdate = () => {
    const v = videoRef.current;
    if (!v || !v.duration) return;
    setProgress((v.currentTime / v.duration) * 100);
  };

  const handleScrub = (value: number) => {
    const v = videoRef.current;
    if (!v || !v.duration) return;
    v.currentTime = (v.duration * value) / 100;
  };

  /* ---------------- time ---------------- */
  const formatTime = (sec: number) => {
  if (!sec || isNaN(sec)) return "0:00";
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60).toString().padStart(2, "0");
  return `${m}:${s}`;
};


  /* ---------------- volume ---------------- */
  const handleVolume = (value: number) => {
    const v = videoRef.current;
    if (!v) return;
    v.volume = value;
    setVolume(value);
  };

  useEffect(() => {
  const v = videoRef.current;
  if (!v) return;
  v.muted = isMuted;
}, [isMuted]);

  /* ---------------- play/pause ---------------- */
  const togglePlay = () => {
  const v = videoRef.current;
  if (!v) return;

  if (!v.paused && !v.ended) {
    v.pause();
    setUserPaused(true);
  } else {
    v.play().catch(() => {});
    setUserPaused(false);
  }
};
  
  /* ---------------- render ---------------- */

 return (
  <div
    style={{
      position: "relative",
      width: "100%",
      height: "100%",
      overflow: "hidden",
    }}
    onMouseEnter={() => setShowControls(true)}
    onMouseLeave={() => setShowControls(false)}
  >
    {/* VIDEO */}
    <video
      ref={videoRef}
      poster={poster}
      muted={isMuted}
      playsInline
      preload="auto"
      controls={false}
      onPointerDown={togglePlay}
      onLoadedMetadata={(e) => {
        emitOrientation(e.currentTarget);
      }}
      onLoadedData={(e) => emitOrientation(e.currentTarget)}
      onCanPlay={(e) => emitOrientation(e.currentTarget)}
      onTimeUpdate={onTimeUpdate}
      onPlay={() => {
  setIsPlaying(true);
  setUserPaused(false);
}}
onPause={() => {
  setIsPlaying(false);
}}
//For the replay this onEnded if I dont want replay remove
onEnded={() => {
  const v = videoRef.current;
  if (!v) return;

  v.currentTime = 0;
  v.play().catch(() => {});
}}
      style={{
        width: "100%",
        height: "100%",
        objectFit: "contain",
        display: "block",
        transform: "translateZ(0)",
        backfaceVisibility: "hidden",
        ...style,
      }}
    />

    {/* CONTROLS */}
    <div
      style={{
        position: "absolute",
        bottom: isLandscape ? 0 : 0,
        left: 0,
        width: "100%",
        background: "rgba(0,0,0,0.65)",
        backdropFilter: "blur(6px)",
        opacity: showControls ? 1 : 0,
        transition: "opacity 200ms ease",
        borderBottomLeftRadius: 12,
        borderBottomRightRadius: 12,
        overflow: "hidden",
      }}
      onClick={(e) => e.stopPropagation()} 
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          width: "100%",
          padding: isLandscape ? "7px 14px" : "5px 8px",
        }}
      >
        {/* LEFT — PLAY */}
        <div style={{ display: "flex", alignItems: "center" }}>
    <button
  onPointerDown={(e) => {
    e.stopPropagation();
    togglePlay();
  }}
  style={controlBtn}
>

  {isPlaying ? <PauseIcon /> : <PlayIcon />}

</button>

        </div>

        {/* CENTER — MAIN SCRUBBER */}
<div
  style={{
    flex: "1 1 auto",
    display: "flex",
    alignItems: "center",
    padding: "0 6px",
    maxWidth: isLandscape ? "70%" : "55%",   // 🔥 limits how wide the scrubber can ever become
    minWidth: 120,     // keeps it usable on small videos
  }}
>
  <input
    type="range"
    min={0}
    max={100}
    value={progress}
    onChange={(e) => handleScrub(Number(e.target.value))}
    className="custom-scrubber"
    style={{
      width: "100%",
      height: 3,
      background: `linear-gradient(
      to right,
      #d8cece 0%,
      #d8cece ${progress}%,
      #555 ${progress}%,
      #555 100%
    )`,
    }}
  />
</div>


        {/* RIGHT — TIME + VOLUME */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            flexShrink: 0,
          }}
        >
{/* TIME */}
<span
  style={{
    fontSize: 11,
    color: "#bbb",
    whiteSpace: "nowrap",
    marginLeft: -2,
    marginRight: 7,
    fontVariantNumeric: "tabular-nums", // 👈 stable width numbers
  }}
>
  {formatTime(videoRef.current?.currentTime || 0)}/
  {formatTime(videoRef.current?.duration || 0)}
</span>


          {/* SPEAKER */}
          <button
  onClick={(e) => {
    e.stopPropagation();
    setIsMuted(!isMuted);
  }}
  style={controlBtnVolume}
>
            {isMuted ? <SpeakerOffIcon /> : <SpeakerOnIcon />}
          </button>

          {/* VOLUME */}
          <input
            type="range"
            min={0}
            max={1}
            step={0.05}
            value={isMuted ? 0 : volume}
            onChange={(e) => {
              const val = Number(e.target.value);
              setIsMuted(val === 0);
              handleVolume(val);
            }}
            className="volume-slider"
            style={{
              width: isLandscape ? 85 : 55,
              height: 2,
              background: `linear-gradient(
      to right,
      #bcb3b3 0%,
      #d8cece ${(isMuted ? 0 : volume) * 100}%,
      #666 ${(isMuted ? 0 : volume) * 100}%,
      #666 100%
    )`,
            }}
          />
        </div>
      </div>
    </div>
  </div>
);

};

const controlBtn: React.CSSProperties = {
  background: "transparent",
  border: "none",
  color: "#bcb4b4",
  borderRadius: 6,
  cursor: "pointer",
  fontSize: 20,

  display: "flex",
  alignItems: "center",
  justifyContent: "center",

  height: 24,       // 👈 locks height
  lineHeight: 1,    // 👈 prevents font expansion
  padding: 0,       // 👈 removes vertical shift
};

const controlBtnVolume: React.CSSProperties = {
  background: "transparent",
  border: "none",
  color: "#bcb4b4",
  borderRadius: 6,
  cursor: "pointer",
  fontSize: 20,

  display: "flex",
  alignItems: "center",
  justifyContent: "center",

  height: 24,
  lineHeight: 1,
  padding: 0,
};


function useMediaOrientation(url: string, isVideo: boolean) {
  const [isLandscape, setIsLandscape] = React.useState<boolean | null>(null);

  React.useEffect(() => {
    let alive = true;
    if (!url) { setIsLandscape(null); return; }

    if (isVideo) {
      const v = document.createElement('video');
      v.preload = 'metadata';
      v.src = url;

      const onMeta = () => {
        if (!alive) return;
        const w = v.videoWidth || 0, h = v.videoHeight || 0;
        setIsLandscape(w && h ? w > h : null);
        cleanup();
      };
      const onErr = () => { if (alive) setIsLandscape(null); cleanup(); };
      const cleanup = () => {
        v.removeEventListener('loadedmetadata', onMeta);
        v.removeEventListener('error', onErr);
      };

      v.addEventListener('loadedmetadata', onMeta);
      v.addEventListener('error', onErr);
      try { v.load(); } catch {}
      return () => { alive = false; cleanup(); };
    }

    const img = new Image();
    img.onload = () => { if (alive) setIsLandscape(img.naturalWidth > img.naturalHeight); };
    img.onerror = () => { if (alive) setIsLandscape(null); };
    img.src = url;

    return () => { alive = false; };
  }, [url, isVideo]);

  return isLandscape;
}

const AutoFitMedia: React.FC<{
  entry: MediaEntry;
  isPrepared: boolean;
  isActive: boolean;
  globalMuted: boolean;
  onClick?: () => void;
  label?: string;
  showMusic?: boolean;
  onMusicClick?: (entry: MediaEntry) => void;
}> = ({ entry, isPrepared, isActive, onClick, label, globalMuted, showMusic, onMusicClick }) => {

  const isVideo = entry.isHls === true && typeof entry.videoId === "string";

  const mediaUrl = isVideo
  ? resolveVideoUrl(entry)
  : (entry.url || '');

  const [isVideoLandscape, setIsVideoLandscape] = useState<boolean | null>(null);
  const isImageLandscape = useMediaOrientation(
  isVideo ? "" : mediaUrl,
  false
);
const isLandscape = isVideo
  ? isVideoLandscape
  : isImageLandscape;

  if (!isPrepared) {
    return (
      <div
        style={{
          height: MEDIA_HEIGHT,
          width: MEDIA_MAX_WIDTH,
          background: "#050505",
          borderRadius: 12,
        }}
      />
    );
  }

  const tileStyle: React.CSSProperties = {
    position: "relative",
    flex: "0 0 auto",
    width: "fit-content",
    maxWidth: MEDIA_MAX_WIDTH,
    height: MEDIA_HEIGHT,
    maxHeight: MEDIA_HEIGHT,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  };

  return (
    <div style={tileStyle}>
      {/* OUTER — allows music to escape bottom */}
      <div
        style={{
          height: "100%",
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          position: "relative",
        }}
      >

        {/* INNER — clips rounded corners */}
        <div
          style={{
            width: "100%",
            height: "100%",
            borderRadius: 12,
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "center",
          }}
        >

          {/* 🎬 VIDEO */}
{isVideo && (
    isLandscape ? (

              /* LANDSCAPE VIDEO STRUCTURE (same as MediaItem) */
              <div style={{ position: "relative", display: "inline-block" }}>
                <HLSPlayer
                  src={mediaUrl}
                  muted={globalMuted}
                  active={isActive}
                  onOrientation={setIsVideoLandscape}
                  isLandscape={true}
                  style={{
                    maxHeight: MEDIA_BOX,
                    maxWidth: "100%",
                    height: "auto",
                    width: "auto",
                    objectFit: "contain",
                    borderRadius: 12,
                  }}
                />

                {/* music bottom */}
                {!!label && showMusic && (
                  <div
                    onClick={(e) => {
                      e.stopPropagation();
                      onMusicClick?.(entry);
                    }}
                    style={{
                      position: "absolute",
                      bottom: -22,
                      left: "50%",
                      transform: "translateX(-50%)",
                      fontSize: 11,
                      color: "#807f7f",
                      whiteSpace: "nowrap",
                      cursor: "pointer",
                      zIndex: 20,
                    }}
                  >
                    🎵 {label}
                  </div>
                )}
              </div>

            ) : (

/* PORTRAIT VIDEO STRUCTURE — FIXED */
<div
  style={{
    position: "relative",
    display: "inline-block",
    borderRadius: 12,
  }}
>
  <HLSPlayer
    src={mediaUrl}
    muted={globalMuted}
    active={isActive}
    onOrientation={setIsVideoLandscape}
    isLandscape={false}
    style={{
      maxHeight: MEDIA_BOX,
      maxWidth: "100%",
      height: "auto",
      width: "auto",
      objectFit: "contain",
      borderRadius: 12,
    }}
  />

  {!!label && showMusic && (
    <div
      onClick={(e) => {
        e.stopPropagation();
        onMusicClick?.(entry);
      }}
      style={{
        position: "absolute",
        bottom: -22,
        left: "50%",
        transform: "translateX(-50%)",
        fontSize: 11,
        color: "#807f7f",
        whiteSpace: "nowrap",
        cursor: "pointer",
        zIndex: 20,
      }}
    >
      🎵 {label}
    </div>
  )}
</div>

            )
          )}

          {/* 🖼️ IMAGE */}
          {!isVideo && mediaUrl && (
            <img
              src={mediaUrl}
              alt="media"
              loading="eager"
              decoding="async"
              onClick={onClick}
              style={
                isLandscape
                  ? {
                      width: MEDIA_BOX,     
                      height: "auto",
                      maxHeight: "100%",
                      maxWidth: "100%",
                      objectFit: "contain",
                      borderRadius: 12,
                      cursor: onClick ? "pointer" : undefined,
                    }
                  : {
                      height: MEDIA_BOX,
                      width: "auto",
                      maxWidth: "100%",
                      objectFit: "contain",
                      borderRadius: 12,
                      cursor: onClick ? "pointer" : undefined,
                    }
              }
            />
          )}

        </div>
      </div>
    </div>
  );
};

const asMediaEntry = (m: any): MediaEntry | null => {
  if (!m) return null;

if (m.isHls === true && typeof m.videoId === "string") {
  return {
    kind: 'video',
    isHls: true,
    videoId: m.videoId,
    hlsUrl: m.hlsUrl ?? m.url ?? null,
    dashUrl: m.dashUrl ?? null,
    posterUrl: m.posterUrl ?? null,
    musicId: m.musicId ?? null,
    musicTitle: m.musicTitle ?? null,
    musicArtist: m.musicArtist ?? null,
  };
}

  const imgUrl = typeof m === "string"
    ? toImageUrl(m)
    : typeof m?.url === "string"
    ? toImageUrl(m.url)
    : typeof m?.path === "string"
    ? toImageUrl(m.path)
    : typeof m?.src === "string"
    ? toImageUrl(m.src)
    : null;

if (imgUrl) {
  return {
    kind: 'image',
    url: imgUrl,
    musicId: m.musicId ?? null,
    musicTitle: m.musicTitle ?? null,
    musicArtist: m.musicArtist ?? null,
  };
}

  return null;
};


const coerceMediaList = (p: any): any[] => {
  const candidates = [
    p?.media, p?.medias, p?.attachments, p?.files, p?.gallery, p?.items
  ];
  const first = candidates.find(v => v != null);
  if (Array.isArray(first)) return first;
  if (first) return [first];
  return [];
};

const formatMediaFlexible = (p: any): MediaEntry[] =>
  coerceMediaList(p).map(asMediaEntry).filter(Boolean) as MediaEntry[];


const mediaLabelOf = (m: MediaEntry): string => {
  const t = (m.musicTitle || '').trim();
  const a = (m.musicArtist || '').trim();
  if (!t && !a) return '';
  return t && a ? `${t} — ${a}` : (t || a);
};



//Profile Card transfer

const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';

const normalizeMe = (u: any) => {
  const rawPicture = u?.picture ?? u?.avatar ?? u?.photoUrl ?? u?.image ?? '';
  const picture =
    typeof rawPicture === 'string' && rawPicture.length > 0
      ? (rawPicture.startsWith('http') ? rawPicture : `${baseUrl}${rawPicture}`)
      : '';

  return {
    profilename: u?.profilename ?? u?.displayName ?? u?.username ?? u?.name ?? '',
    location: u?.location ?? u?.city ?? '',
    bio: u?.bio ?? u?.about ?? u?.description ?? '',
    picture,
    email: u?.email ?? '',
  };
};

const MAX_POSTS_IN_MEMORY = 180;

const VisitorPostings = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  
  const [posts, setPosts] = useState<Post[]>([]);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [globalMuted, setGlobalMuted] = useState(true);
  const [isVisitorSidebarOpen, setVisitorSidebarOpen] = useState(true);
  const [visitor, setVisitor] = useState<{profilename:string; location:string; bio:string; picture:string; email:string;} | null>(null);
  const [pageIndex, setPageIndex] = useState(0);
  const feedRef = useRef<HTMLDivElement | null>(null);
  //Presentation
  const [presentationPost, setPresentationPost] = useState<Post | null>(null);
  const [presentationIndex, setPresentationIndex] = useState<number | null>(null);


  const [isFetching, setIsFetching] = useState(false);
  const LIMIT = 80;

  const [viewRestricted, setViewRestricted] = useState(false); // 👈 NEW

  const loc = useLocation();

// hold the post id we want to focus
const [focusPostId, setFocusPostId] = useState<string | null>(null);

// map of postId -> element
const postRefs = useRef<Record<string, HTMLDivElement | null>>({});

  // --- music hover state (per media tile) ---
const [hoveredKey, setHoveredKey] = useState<string | null>(null);
const hoverTimeoutRef = useRef<number | null>(null);

//Loading
const isInitialLoading =
  isFetching &&
  posts.length === 0 &&
  !viewRestricted &&
  visitor === null;


//Report Button
const [menuOpenPostId, setMenuOpenPostId] = useState<string | null>(null);
const [reportOpen, setReportOpen] = useState(false);
const [reportPostId, setReportPostId] = useState<string | null>(null);
const [selectedReason, setSelectedReason] = useState<string | null>(null);
const [reportStep, setReportStep] = useState<"list" | "details" | "done">("list");
const [details, setDetails] = useState("");

const menuRef = useRef<HTMLDivElement | null>(null);

useEffect(() => {
  const handleClickOutside = (e: MouseEvent) => {
    if (!menuRef.current) return;
    if (!menuRef.current.contains(e.target as Node)) {
      setMenuOpenPostId(null);
    }
  };

  if (menuOpenPostId) {
    document.addEventListener("mousedown", handleClickOutside);
  }

  return () => {
    document.removeEventListener("mousedown", handleClickOutside);
  };
}, [menuOpenPostId]);

const handleReportPost = (postId: string) => {
  setMenuOpenPostId(null);
  setReportPostId(postId);
  setReportOpen(true);
  setReportStep("list");
};

const submitReport = async () => {
  if (!selectedReason || !reportPostId) return;

  try {
    await api.post("/block-report/report/post", {
      postId: reportPostId,
      reasonKey: selectedReason,
      details: details || undefined,
    });

    setReportStep("done");

    setTimeout(() => {
      setReportOpen(false);
      setReportPostId(null);
      setSelectedReason(null);
      setDetails("");
      setReportStep("list");
    }, 1200);
  } catch (err) {
    console.error(err);
    alert("Failed to report post.");
  }
};
//Report Button End

const handleHoverEnter = (key: string) => {
  if (hoverTimeoutRef.current) {
    window.clearTimeout(hoverTimeoutRef.current);
    hoverTimeoutRef.current = null;
  }
  setHoveredKey(key);
};

const handleHoverLeave = () => {
  // linger a bit so it doesn't disappear as you move toward controls
  hoverTimeoutRef.current = window.setTimeout(() => {
    setHoveredKey(null);
    hoverTimeoutRef.current = null;
  }, 1500);
};


useEffect(() => {
  return () => {
    if (hoverTimeoutRef.current) {
      window.clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
  };
}, []);

//Only one Video at a time
useEffect(() => {
  const onPlay = (e: Event) => {
    const el = e.target as HTMLVideoElement | null;
    if (!el || el.tagName !== 'VIDEO') return;

    // Pause every other <video> on the page
    document.querySelectorAll('video').forEach((v) => {
      if (v !== el) {
        try { v.pause(); } catch {}
      }
    });
  };

  // Use capture phase to catch plays from custom players too
  document.addEventListener('play', onPlay, true);
  return () => document.removeEventListener('play', onPlay, true);
}, []);


const handleOpenMusic = async (entry: MediaEntry) => {
  // 1️⃣ If backend already gave us a musicId, just go directly
  if (entry.musicId) {
    navigate(`/musicdetails/${entry.musicId}`);
    return;
  }

  // 2️⃣ Fallback: search by either title OR artist (whichever exists),
  //    instead of "title + artist" concatenation
  const q = (entry.musicTitle || entry.musicArtist || '').trim();
  if (!q) {
    console.warn('[VisitorPostings.handleOpenMusic] no title/artist on entry', entry);
    return;
  }

  try {
    const res = await api.get('/music', { params: { q } });
    const list = Array.isArray(res.data) ? res.data : (res.data?.tracks || []);
    if (!list.length) {
      console.warn('[VisitorPostings.handleOpenMusic] no match for', q);
      return;
    }

    navigate(`/musicdetails/${list[0]._id}`);
  } catch (e) {
    console.error('[VisitorPostings.handleOpenMusic] music lookup failed:', e);
  }
};

  const { likedIds, likeCounts, likePost, unlikePost, refreshLikes, } = useLikes();

  const fetchVisitorPosts = async (useOffset: number, append = false) => {
  if (!userId) return;
  try {
    setIsFetching(true);
    const res = await api.get(`/visitor-postings/${userId}`, {
      params: { offset: useOffset, limit: LIMIT },
    });

    const raw: any[] = res.data || [];

    const normalized: Post[] = raw.map((p: any) => {
      const likeKey = likeKeyFromRow(p);
    
      const likes =
        typeof p.likes === 'number'
          ? p.likes
          : typeof p.totalLikes === 'number'
          ? p.totalLikes
          : 0;
    
      return {
        ...p,
        _id: String(p._id),                 // real post id
        likeKey,                          
        media: formatMediaFlexible(p),
        captions: [],
        likes,
        likedByUser: likedIds.has(likeKey) || !!p.likedByUser,
      };
    });
    
    
setPosts(prev => {
  const base = append ? prev : [];
  const merged = [...base, ...normalized];
  const byId = new Map(merged.map(p => [p._id, p]));
  return Array.from(byId.values()).slice(-MAX_POSTS_IN_MEMORY);
});

    setOffset(useOffset + LIMIT);
    setHasMore(raw.length === LIMIT);
    setViewRestricted(false); // ✅ successfully loaded → not restricted
  } catch (err: any) {
    console.error('❌ Failed to fetch visitor posts:', err);
    const status = err?.response?.status;
    if (status === 403 || status === 404) {
      // we are blocked or user/posts hidden → treat as "no posts" but normal layout
      setViewRestricted(true);
      setPosts([]);       // ensure empty
      setHasMore(false);  // no infinite loading
    }
  } finally {
    setIsFetching(false);
  }
};

//Refresh Likes
useEffect(() => {
  // When entering visitor postings, sync global likes from backend
  refreshLikes();
}, [refreshLikes]);

  
  // Activate Profile Card
  useEffect(() => {
    (async () => {
      try {
        if (!userId) return;
        const res = await api.get(`/users/${userId}`); // <-- adjust if your endpoint differs
        setVisitor(normalizeMe(res.data));
        setViewRestricted(false);
      } catch (e: any) {
        console.warn('Failed to load visitor profile', e);
        const status = e?.response?.status;
        if (status === 403 || status === 404) {
          // blocked / hidden / deleted: keep a generic card with ghost avatar
          setVisitor({
            profilename: t("visitorPostings.restricted.name") || "User",
            location: "",
            bio: t("visitorPostings.restricted.bio") || "",
            picture: "",
            email: "",
          });
          setViewRestricted(true);
        } else {
          setVisitor(null);
        }
      }
    })();
  }, [userId, t]);
  

  
  useEffect(() => {
  const onFocus = () => refreshLikes();
  const onVisibility = () => {
    if (document.visibilityState === "visible") refreshLikes();
  };

  window.addEventListener("focus", onFocus);
  document.addEventListener("visibilitychange", onVisibility);

  return () => {
    window.removeEventListener("focus", onFocus);
    document.removeEventListener("visibilitychange", onVisibility);
  };
}, [refreshLikes]);


useEffect(() => {
  setPageIndex(prev => Math.min(prev, Math.max(0, posts.length - 1)));
}, [posts.length]);

    
  useEffect(() => {
    if (!userId) return;

    setPosts([]);
    setOffset(0);
    setHasMore(true);

    fetchVisitorPosts(0, false); // initial page with explicit offset 0
  }, [userId]);

  
 useEffect(() => {
  const next = posts[pageIndex + 1];
  if (!next) return;

  next.media?.forEach(m => {
    if (!m.isHls && m.url) {
      const img = new Image();
      img.src = m.url;
      img.decoding = "async";
      img.loading = "eager";
    }
  });
}, [pageIndex, posts]);


useEffect(() => {
  const handler = (e: KeyboardEvent) => {
    const el = feedRef.current;
    if (!el) return;

    // ignore typing inside inputs
    const tag = (e.target as HTMLElement)?.tagName;
    if (tag === "INPUT" || tag === "TEXTAREA" || (e.target as HTMLElement)?.isContentEditable) {
      return;
    }

    if (e.key === "ArrowDown") {
      e.preventDefault();
      el.scrollBy({
        top: el.clientHeight,
        behavior: "smooth",
      });
    }

    if (e.key === "ArrowUp") {
      e.preventDefault();
      el.scrollBy({
        top: -el.clientHeight,
        behavior: "smooth",
      });
    }
  };

  window.addEventListener("keydown", handler);
  return () => window.removeEventListener("keydown", handler);
}, []);



  useEffect(() => {
    const fromHash = loc.hash?.startsWith('#post-') ? loc.hash.slice('#post-'.length) : null;
    const fromQuery = new URLSearchParams(loc.search).get('post');
    const fromState = (loc.state as any)?.focusPostId || null;
  
    setFocusPostId(fromHash || fromQuery || fromState || null);
  }, [loc]);

  useEffect(() => {
    if (!focusPostId) return;
    const el = postRefs.current[focusPostId];
    if (!el) return;
  
    // smooth scroll to the post
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  
    // pulse highlight
    el.animate(
      [
        { boxShadow: '0 0 0 0 rgba(37,99,235,0.0)' },
        { boxShadow: '0 0 0 6px rgba(37,99,235,0.35)' },
        { boxShadow: '0 0 0 0 rgba(37,99,235,0.0)' },
      ],
      { duration: 1200, easing: 'ease-in-out' }
    );
  }, [focusPostId, posts.length]); // re-run when posts change

  //Loading
  const visitorName = visitor?.profilename ?? 'user';

  useEffect(() => {
  const styleId = 'spin-keyframes';

  if (document.getElementById(styleId)) return;

  const style = document.createElement('style');
  style.id = styleId;
  style.innerHTML = `
    @keyframes spin {
      from { transform: rotate(0deg); }
      to { transform: rotate(360deg); }
    }
  `;

  document.head.appendChild(style);
  return () => style.remove();
}, []);

  
  const handleLike = async (likeKey: string) => {
    const target = String(likeKey);
    try {
      const newLikes = await likePost(target);
      setPosts(prev =>
        prev.map(p =>
          p.likeKey === target
            ? {
                ...p,
                likedByUser: true,
                likes: newLikes ?? p.likes,
              }
            : p
        )
      );
    } catch (err) {
      console.error('❌ Failed to like post:', err);
    }
  };
  
  const handleUnlike = async (likeKey: string) => {
    const target = String(likeKey);
    try {
      const newLikes = await unlikePost(target);
      setPosts(prev =>
        prev.map(p =>
          p.likeKey === target
            ? {
                ...p,
                likedByUser: false,
                likes: newLikes ?? p.likes,
              }
            : p
        )
      );
    } catch (err) {
      console.error('❌ Failed to unlike post:', err);
    }
  };
  
  // 🔥 FEED VIRTUALIZATION (MUST BE ABOVE RETURN)
const start = Math.max(0, pageIndex - 1);
const end = Math.min(posts.length, pageIndex + 2);
const visiblePosts = posts.slice(start, end);

const pageHeight = feedRef.current?.clientHeight || window.innerHeight;

const SIDEBAR_WIDTH = 270;

return (
    
<div style={{ display: 'flex', height: '100vh', overflow: 'hidden', backgroundColor: '#000' }}>
  {isVisitorSidebarOpen && (
    <SidebarVisitor onClose={() => setVisitorSidebarOpen(false)} />
  )}

       {/* Left bottom terms privacy help buttons */}
<div
  style={{
    position: "fixed",
    left: 18,
    bottom: 8,
    width: 230,
    zIndex: 50,
    fontSize: 10,
    color: "#555",
    lineHeight: 1.6,
    textAlign: "left",
    pointerEvents: "auto",
  }}
>
  <span
    style={{ cursor: "pointer" }}
    onClick={() => navigate("/helpsupportstart")}
  >
    {t("mainSidebar.footer.helpSupport")}
  </span>

  {" · "}

  <span
    style={{ cursor: "pointer" }}
    onClick={() => navigate("/legal/privacypolicy")}
  >
    {t("mainSidebar.footer.privacyPolicy")}
  </span>

  {" · "}

  <span
    style={{ cursor: "pointer" }}
    onClick={() => navigate("/legal/termsofuse")}
  >
    {t("mainSidebar.footer.termsOfUse")}
  </span>
</div>

  {/* Feed */}
<div
  ref={feedRef}
  onScroll={(e) => {
    const el = e.currentTarget;
    const rawIdx = Math.round(el.scrollTop / el.clientHeight);
    const idx = visitor ? rawIdx - 1 : rawIdx;
    if (idx !== pageIndex) setPageIndex(idx);

    const nearBottom =
      el.scrollTop + el.clientHeight >= el.scrollHeight - 200;

    if (nearBottom && !isFetching && hasMore) {
      fetchVisitorPosts(offset, true);
    }
  }}
  style={{
    flexGrow: 1,
    marginLeft: `${SIDEBAR_WIDTH}px`,
    overflowY: 'auto',
    overflowX: 'hidden',
    height: '100vh',
    scrollSnapType: 'y mandatory',
    backgroundColor: '#000',
  }}
>

     {/* ✅ NEW: Visitor header card (same look as Profile card) */}
{visitor && (
  <section
    style={{
      height: '100vh',
      scrollSnapAlign: 'start',
      position: 'relative',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}
  >
    {/* Profile card */}
    <div
      style={{
        ...headerStyles.card,
        width: '100%',
padding: '0 20px',
marginLeft: 0,
boxSizing: 'border-box',
      }}
    >
      <div style={headerStyles.cardGrid}>
        <div style={{ textAlign: 'center' }}>
          <div style={headerStyles.avatarBox}>
            {visitor.picture ? (
              <img src={visitor.picture} alt="profile" style={headerStyles.avatarImg} />
            ) : (
              <div style={headerStyles.avatarPlaceholder}>👻</div>
            )}
          </div>
        </div>

        <div style={{ display: 'grid', gap: 12 }}>
          <div>
            <label style={headerStyles.label}>
              <strong>{t("visitorPostings.header.name")}</strong>
            </label>
            <div style={headerStyles.roInput}>{visitor.profilename || '—'}</div>
          </div>

          <div>
            <label style={headerStyles.label}>
              {t("visitorPostings.header.location")}
            </label>
            <div style={headerStyles.roInput}>{visitor.location || '—'}</div>
          </div>

          <div>
            <label style={headerStyles.label}>
              {t("visitorPostings.header.about")}
            </label>
            <div style={{ ...headerStyles.roInput, minHeight: 48 }}>
              {visitor.bio || '—'}
            </div>
          </div>
        </div>
      </div>
    </div>

    {/* SIMPLE arrow hint */}
    <div
      style={{
        position: 'absolute',
        bottom: 30,
        left: 0,
        right: 0,
        textAlign: 'center',
        color: '#aaa',
        fontSize: 14,
      }}
    >
      <div>
        Scroll down to view <strong>{visitor.profilename}</strong>'s posts
      </div>
      <div style={{ fontSize: 26, marginTop: 4 }}>↓</div>
    </div>
  </section>
)}

{isInitialLoading ? (
  <div
    style={{
      height: '100dvh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexDirection: 'column',
      gap: 14,
      color: '#bbb',
      fontSize: 18,
      letterSpacing: 0.4,
    }}
  >
    <div
      style={{
        width: 42,
        height: 42,
        borderRadius: '50%',
        border: '3px solid rgba(255,255,255,0.15)',
        borderTop: '3px solid #fff',
        animation: 'spin 0.9s linear infinite',
      }}
    />

    <div>
      Loading {visitorName}'s profile…

    </div>
  </div>

) : posts.length > 0 ? (

<>
{start > 0 && (
  <div style={{ height: start * pageHeight }} />
)}


  {visiblePosts.map((post, localIdx) => {
    const idx = start + localIdx;
    const isActive = idx === pageIndex;
    const inWindow = true;

    const likeKey = post.likeKey || post._id;

const liked =
  likedIds.has(likeKey) ??
  post.likedByUser ??
  false;

const likeCount =
  likeCounts.get(likeKey) ??
  post.likes ??
  0;


    return (
      <div
        key={post._id}
        id={`post-${post._id}`}
        ref={(el) => { postRefs.current[post._id] = el; }}
        style={postCardStyle}
      >


      {post.title && (
        <h3 style={{ color: post.titleColor || '#fff' }}>{post.title}</h3>
      )}

      <div style={metaRightStyle}>
        {/* Metadata + actions */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            marginTop: '5px',
            marginBottom: '2px',
            gap: '16px',
            paddingLeft: '30px',
            flexWrap: 'wrap',
          }}
        >
          <div
  style={{
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    color: '#988f8f',
    fontSize: 11,
  }}
>
  <div
    ref={menuOpenPostId === post._id ? menuRef : null}
    style={{ position: "relative", display: "inline-flex", alignItems: "center" }}
  >
    <button
      onClick={(e) => {
        e.stopPropagation();
        setMenuOpenPostId((prev) => (prev === post._id ? null : post._id));
      }}
      style={{
        background: "transparent",
        border: "none",
        color: "#bbb",
        fontSize: 18,
        cursor: "pointer",
        padding: "2px 6px",
        lineHeight: 1,
      }}
    >
      ⋯
    </button>

    {menuOpenPostId === post._id && (
      <div
        style={{
          position: "absolute",
          right: "100%",
          top: "50%",
          transform: "translateY(-50%)",
          marginRight: 8,
          background: "#111",
          border: "1px solid #333",
          borderRadius: 6,
          padding: "3px 5px",
          zIndex: 50,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          whiteSpace: "nowrap",
        }}
      >
        <button
          onClick={(e) => {
            e.stopPropagation();
            handleReportPost(post._id);
          }}
          style={{
            padding: "3px 6px",
            background: "none",
            border: "none",
            color: "#ff4d4d",
            cursor: "pointer",
            fontSize: 11,
            lineHeight: 1.1,
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
            whiteSpace: "nowrap",
            borderRadius: 4,
          }}
        >
          🚩 Report Post
        </button>
      </div>
    )}
  </div>

  <button
  type="button"
  onClick={(e) => {
    e.stopPropagation();

    const city = post.location?.city?.trim();
    const country = post.location?.country?.trim();

    if (!city || !country) return;

    navigate(
      `/location-collection?city=${encodeURIComponent(city)}&country=${encodeURIComponent(country)}`
    );
  }}
  style={{
    background: "transparent",
    border: "none",
    padding: 0,
    margin: 0,
    color: "#988f8f",
    fontSize: 11,
    cursor: post.location?.city && post.location?.country ? "pointer" : "default",
    whiteSpace: "nowrap",
  }}
  title={
    post.location?.city && post.location?.country
      ? `${post.location.city}, ${post.location.country}`
      : "Unknown location"
  }
  disabled={!post.location?.city || !post.location?.country}
>
  📍 {post.location?.city && post.location?.country
    ? `${post.location.city}, ${post.location.country}`
    : 'Unknown location'}
</button>

  <span>Posted: {new Date(post.createdAt).toLocaleString()}</span>
</div>


          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <button
              type="button"
              onClick={() => {
                setPresentationPost(post);
                setPresentationIndex(0);
              }}
              style={actionButtonStyle}
              title="Open presentation overlay"
            >
              {t("visitorPostings.buttons.presentation")}
            </button>


<button
  onClick={() =>
    liked ? handleUnlike(likeKey) : handleLike(likeKey)
  }
  style={{
    ...actionButtonStyle,
    backgroundColor: liked ? '#ffccd5' : '#111',
    color: liked ? '#a00' : '#bbb',
  }}
>
  {liked
    ? `💔 Unlike (${likeCount})`
    : `❤️ Like (${likeCount})`}
</button>

          </div>
        </div>
      </div>

      {/* Media section */}
      <div style={mediaScrollContainerStyle}>
        {(post.media as MediaEntry[]).map((entry, index) => {
          const video = entry.isHls === true;

          const label = mediaLabelOf(entry);
          const hk = `${post._id}-${index}`;

          return (
            <div
              key={`${post._id}-${index}`}
              style={mediaItemWrapStyle}
              role={video ? 'group' : 'button'}
              tabIndex={video ? -1 : 0}
              onClick={!video ? () => {
                setPresentationPost(post);
                setPresentationIndex(index);
              } : undefined}
              onMouseEnter={() => handleHoverEnter(hk)}
              onMouseLeave={handleHoverLeave}
            >
              <AutoFitMedia
  entry={entry}
  isPrepared={inWindow}
  isActive={isActive}
  globalMuted={globalMuted}
  onClick={() => {

                  setPresentationPost(post);
                  setPresentationIndex(index);
                }}
                label={label}
                showMusic={hoveredKey === hk}
                onMusicClick={handleOpenMusic}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
})} 
   {end < posts.length && (
  <div style={{ height: (posts.length - end) * pageHeight }} />
)}

</>

) : (

  <div
    style={{
      ...postCardStyle,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      minHeight: 160,
      textAlign: "center",
      color: "#9ca3af",
    }}
  >
    <div style={{ fontSize: 18, marginBottom: 6 }}>
      {t("visitorPostings.empty.noPosts") || "No posts yet..."}
    </div>
  </div>

     )}
  </div>
  <Modal
  open={reportOpen}
  onClose={() => {
    setReportOpen(false);
    setReportPostId(null);
    setSelectedReason(null);
    setDetails("");
    setReportStep("list");
  }}
  title="Report this post"
  width={420}
>
  {reportStep === "list" && (
    <>
      {[
        { key: "spam", label: "Spam" },
        { key: "hate", label: "Hate speech" },
        { key: "harassment", label: "Harassment or bullying" },
        { key: "sexual content", label: "Sexual content" },
        { key: "scam or fraud", label: "Scam or fraud" },
        { key: "violence", label: "Violence" },
        { key: "self-harm", label: "Self-harm" },
        { key: "other", label: "Other" },
      ].map((r) => (
        <button
          key={r.key}
          onClick={() => {
            setSelectedReason(r.key);
            setReportStep("details");
          }}
          style={{
            width: "100%",
            padding: "10px",
            marginBottom: 8,
            background: "#111",
            border: "1px solid #333",
            borderRadius: 6,
            color: "#ddd",
            textAlign: "left",
            cursor: "pointer",
          }}
        >
          {r.label}
        </button>
      ))}
    </>
  )}

  {reportStep === "details" && (
    <>
      <textarea
        value={details}
        onChange={(e) => setDetails(e.target.value)}
        placeholder="(Optional) Describe the issue..."
        style={{
          width: "100%",
          minHeight: 80,
          borderRadius: 6,
          padding: 10,
          background: "#000",
          border: "1px solid #333",
          color: "#fff",
          marginBottom: 12,
        }}
      />

      <button
        onClick={submitReport}
        style={{
          width: "100%",
          padding: "10px",
          background: "#ff4d4d",
          border: "none",
          borderRadius: 6,
          color: "#fff",
          cursor: "pointer",
          fontWeight: 600,
        }}
      >
        Submit Report
      </button>
    </>
  )}

  {reportStep === "done" && (
    <div style={{ textAlign: "center" }}>
      ✅ Report submitted
    </div>
  )}
</Modal>
        
  {presentationPost && presentationIndex !== null && (
  <VisitorPostingsPresentation
    post={presentationPost}
    startIndex={presentationIndex}
    onClose={() => {
      setPresentationPost(null);
      setPresentationIndex(null);
        }}
        />
      )}
  </div>
 );
 };

// --- Media sizing (same as MyPostings) ---
const MEDIA_BOX = "calc(100vh - 80px)";
const MEDIA_HEIGHT = MEDIA_BOX;
const MEDIA_MAX_WIDTH = MEDIA_BOX;   // 🔥 THIS is the key

const postCardStyle: React.CSSProperties = {
  border: 'none',
  marginLeft: 0,
  width: '100%',
  padding: '10px 8px 1px 8px',
  height: '100vh',
  scrollSnapAlign: 'start',
  boxSizing: 'border-box',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
};

  
const mediaScrollContainerStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'row',
  overflowX: 'auto',
  overflowY: 'hidden',
  gap: '30px',
  paddingTop: '15px',
  paddingBottom: '1px',
  paddingLeft: '18px',
  scrollSnapType: 'x mandatory',
  WebkitOverflowScrolling: 'touch',
  flex: 1,
  minHeight: 0,
  scrollbarWidth: "none",
};



  const mediaItemWrapStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    flex: '0 0 auto',
    scrollSnapAlign: 'center',
    height: '100%',
    maxWidth: MEDIA_MAX_WIDTH,
    maxHeight: MEDIA_HEIGHT,
  };
  
  const actionButtonStyle: React.CSSProperties = {
    backgroundColor: '#111',
    color: '#988f8f',
    border: '1px solid #ccc',
    borderRadius: '6px',
    padding: '2px 4px',
    fontSize: '12px',
    cursor: 'pointer',
  };

const headerStyles: Record<string, React.CSSProperties> = {
  headerTop: { marginBottom: 2 },
  brand: { fontSize: 28, color: '#e6e9f3', margin: 0 },
  subtext: { color: '#a6adbb', marginTop: 8 },

  card: {
    background: '#0f1115',
    border: '1px solid #1f2330',
    borderRadius: 16,
    padding: 24,
    boxShadow: '0 12px 30px rgba(0,0,0,0.25)',
  },
  cardGrid: {
    display: 'grid',
    gridTemplateColumns: '240px 1fr',
    gap: 24,
  },

  avatarBox: {
    position: 'relative',
    width: 180,
    height: 180,
    margin: '8px auto 0',
    borderRadius: 16,
    overflow: 'hidden',
    border: '1px solid rgb(38, 29, 162)',
    background: 'rgb(38, 29, 162)',
  },
  avatarImg: { width: '100%', height: '100%', objectFit: 'cover' },
  avatarPlaceholder: {
    width: '100%', height: '100%',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 56, color: '#e6e9f3',
    background: 'linear-gradient(180deg,rgb(38, 29, 162))',
  },

  label: { color: '#cfd6e6', fontSize: 13, marginBottom: 6 },
  roInput: {
    padding: '10px 12px',
    borderRadius: 10,
    border: '1px solid #2a2f3e',
    background: '#0b0f1a',
    color: '#e6e9f3',
    whiteSpace: 'pre-wrap',
  },
}; 

const metaRightStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-end',
  gap: '10px',
  marginBottom: '2px',
  paddingRight: '30px', // align with media right edge
};

  
export default VisitorPostings;
